import { createApp } from 'vue';
import {
  primevueFrenchLocale,
  getAppConfig,
  getMinsConfigMap,
  getZoomUnitMin,
  buildMapMessage,
  getUnitsDescription,
  getEndsUnitsDescription,
  getStopsUnitsDescription,
  getDefaultValues,
  getDateConfigMap,
} from '@/utils/settings';
import { formatDate } from '@/utils/dateHelpers';

import * as Sentry from '@sentry/vue';
import { BrowserTracing } from '@sentry/tracing';
import PrimeVue from 'primevue/config';
import Tooltip from 'primevue/tooltip';
import App from './App.vue';
import router from './router';

import VueKeyCloak from './plugins/vue-keycloak';

import store from './store';

// import 'primevue/resources/themes/saga-blue/theme.css';
import './styles/_customized_primevue_theme.css';
import 'primevue/resources/primevue.min.css';
import 'primeicons/primeicons.css';
import 'primeflex/primeflex.css';

import '@/styles/style.css';

const app = createApp(App);

// Init Sentry integration
if (process.env.VUE_APP_ENVIRONMENT_NAME !== 'local') {
  Sentry.init({
    app,
    dsn: 'https://f2138969995a46978836879039289c7b@sentry.makina-corpus.net//39',
    integrations: [
      new BrowserTracing({
        routingInstrumentation: Sentry.vueRouterInstrumentation(router),
      }),
    ],
    // Set tracesSampleRate to 1.0 to capture 100%
    // of transactions for performance monitoring.
    // We recommend adjusting this value in production
    tracesSampleRate: 1.0,
    logErrors: true,
    environment: process.env.VUE_APP_ENVIRONMENT_NAME,
  });
}

app.use(VueKeyCloak, {
  config: {
    url: `${process.env.VUE_APP_KEYCLOAK_ROOT}/auth`,
    realm: process.env.VUE_APP_KEYCLOAK_REALM,
    clientId: process.env.VUE_APP_KEYCLOAK_CLIENTID,
    sslRequired: 'external',
    verifyTokenAudience: true,
  },
  init: {
    /*
      set to false because modern browser cut the cross-origin cookies behavior,
      so this iframe will not work and generates warnings in console
      https://www.keycloak.org/docs/latest/securing_apps/index.html#_modern_browsers
      use short access token lifetime instead because the iframe wont be able to
      detect parallel logouts.
    */
    checkLoginIframe: false,
    onLoad: 'login-required',
  },
});
app.use(router);
app.use(PrimeVue, { locale: primevueFrenchLocale });
app.directive('tooltip', Tooltip);

// Load configuration from the server (or use a default value if request fail)
getAppConfig()
  .then(config => {
    app.config.globalProperties.serverConfiguration = config;

    // Compute app config variables
    const minsConfigMap = getMinsConfigMap(app.config.globalProperties.serverConfiguration);
    const datesBoundConfig = getDateConfigMap(app.config.globalProperties.serverConfiguration);
    const zoomUnitMin = getZoomUnitMin(minsConfigMap);
    const zoomMessages = buildMapMessage(zoomUnitMin);
    const zoomMessagesNoDataOnly = buildMapMessage(zoomUnitMin.slice(0, 1));
    const fluxUnitsDescription = getUnitsDescription(minsConfigMap);
    const endsUnitsDescription = getEndsUnitsDescription(minsConfigMap);
    const stopsUnitsDescription = getStopsUnitsDescription(minsConfigMap);
    const minDate = datesBoundConfig.get('prnsn_config_earliest_date');
    const maxDate = datesBoundConfig.get('prnsn_config_latest_date');

    const {
      fluxTracksDefaultMin,
      fluxUsersDefaultMin,
      fluxDefaultUnit,
      endsMin,
      endsDefaultUnit,
      stopsMin,
      stopsDefaultUnit,
    } = getDefaultValues(fluxUnitsDescription, endsUnitsDescription, stopsUnitsDescription);

    store.updateMinTracksFlux(fluxTracksDefaultMin);
    store.updateMinUsersFlux(fluxUsersDefaultMin);
    store.updateMinStops(stopsMin);
    store.updateMinEnds(endsMin);
    store.updateCountFlux(fluxDefaultUnit);
    store.updateCountStops(stopsDefaultUnit);
    store.updateCountEnds(endsDefaultUnit);
    store.updateWindowOpen(formatDate(minDate));
    store.updateWindowClose(formatDate(maxDate));
    store.updateWindowOpen2(formatDate(minDate));
    store.updateWindowClose2(formatDate(maxDate));
    store.updateYearMin(minDate.getFullYear());
    store.updateYearMax(maxDate.getFullYear());

    // Set them as globalProperties
    app.config.globalProperties.$zoomUnitMin = zoomUnitMin;
    app.config.globalProperties.$zoomMessages = zoomMessages;
    app.config.globalProperties.$zoomMessagesNoDataOnly = zoomMessagesNoDataOnly;
    app.config.globalProperties.$zoomMessages = zoomMessages;
    app.config.globalProperties.$fluxUnitsDescription = fluxUnitsDescription;
    app.config.globalProperties.$endsUnitsDescription = endsUnitsDescription;
    app.config.globalProperties.$stopsUnitsDescription = stopsUnitsDescription;
    app.config.globalProperties.$minDate = minDate;
    app.config.globalProperties.$maxDate = maxDate;

    app.mount('#app');
  });
